import { useAuth } from "../../../core/hooks/useAuth";
import { useHandleCourses } from "../../../core/hooks/useHandleCourses";
import {
  useHandleMultipleGrades,
  useMultipleGradeTables,
} from "../../../core/hooks/useHandleGrade";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import ProfileCard from "../common/profileCard";
import StudentSidebar from "../common/studentSidebar";
import { Modal, Table } from "antd";
import { useState } from "react";
import { domParser } from "../../../core/common/ults/domParser";
import { useTranslation } from "react-i18next";

interface GradeItem {
  itemtype: string;
  iteminstance: number;
  gradeformatted: string;
}

interface UserGrade {
  usergrades: Array<{
    gradeitems: GradeItem[];
  }>;
}

interface Course {
  id: number;
  fullname: string;
}

interface GradeTableItem {
  itemname: string;
  graderaw?: number | null;
  percentageformatted?: string;
  weightformatted?: string;
  rankformatted?: string;
  rangeformatted?: string;
}

const StudentGrades = () => {
  const { moodleUser } = useAuth();
  const { enrolledCourses, isEnrolledCoursesLoading } = useHandleCourses(
    moodleUser?.id
  );
  const courseList = Array.isArray(enrolledCourses) ? enrolledCourses : [];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<{
    courseName: string;
    courseId: number;
  } | null>(null);
  const { t } = useTranslation();

  // Sử dụng hook để lấy điểm của nhiều khóa học
  const { gradeQueries } = useHandleMultipleGrades(
    moodleUser?.id,
    courseList
  );
  const { gradeTableQueries } = useMultipleGradeTables(
    moodleUser?.id,
    courseList
  );

  // Hàm kiểm tra điểm số có phải là số hay không
  const isValidGrade = (grade: string): boolean => {
    // Loại bỏ dấu phẩy và khoảng trắng
    const cleanGrade = grade.replace(/,/g, "").trim();
    // Kiểm tra xem có phải là số không
    return !isNaN(Number(cleanGrade));
  };

  const showGradeDetails = (
    course: Course,
    gradeData: UserGrade | undefined
  ) => {
    setSelectedCourse({
      courseName: course.fullname,
      courseId: course.id,
    });
    setIsModalOpen(true);
  };

  const columns = [
    {
      title: t("student.grades.table.activity"),
      dataIndex: "activity",
      key: "activity",
      className: "text-center",
    },
    {
      title: t("student.grades.table.weight"),
      dataIndex: "weight",
      key: "weight",
      className: "text-center",
    },
    {
      title: t("student.grades.table.grade"),
      dataIndex: "grade",
      key: "grade",
      className: "text-center",
    },
    {
      title: t("student.grades.table.passgrade"),
      dataIndex: "gradepass",
      key: "gradepass",
      className: "text-center",
    },

    {
      title: t("student.grades.table.range"),
      dataIndex: "range",
      key: "range",
      className: "text-center",
    },
    // {
    //   title: t("student.grades.table.percentage"),
    //   dataIndex: "percentage",
    //   key: "percentage",
    //   className: "text-center",
    // },
    {
      title: t("student.grades.table.rank"),
      dataIndex: "rank",
      key: "rank",
      className: "text-center",
    },
    // {
    //   title: t("student.grades.table.feedback"),
    //   dataIndex: "feedback",
    //   key: "feedback",
    //   className: "text-center",
    // },
  ];

  const styles = `
    .grade-row {
      transition: all 0.3s ease;
    }
    .grade-row:hover {
      background-color: #f8f8f8 !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .grade-row:hover td {
      color: #1890ff !important;
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="content">
        <div className="container">
          <ProfileCard />
          <div className="row">
            <StudentSidebar />
            <div className="col-lg-9">
              <Breadcrumb title={t("student.grades.title")} />
              <div className="page-title d-flex align-items-center justify-content-between">
                <h5>{t("student.grades.title")}</h5>
              </div>
              <div className="table-responsive rounded">
                <table className="table" style={{ width: "100%" }}>
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: "70%" }}>{t("student.grades.courseName")}</th>
                      <th style={{ width: "30%" }}>{t("student.grades.grade")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isEnrolledCoursesLoading ? (
                      <tr>
                        <td colSpan={2} className="text-center">
                          {t("student.grades.loading")}
                        </td>
                      </tr>
                    ) : courseList.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="text-center">
                          {t("student.grades.noCourses")}
                        </td>
                      </tr>
                    ) : (
                      courseList.map((course: Course, index: number) => {
                        const gradeQuery = gradeQueries[index];
                        const gradeData = gradeQuery.data as
                          | UserGrade
                          | undefined;
                        const courseTotal =
                          gradeData?.usergrades?.[0]?.gradeitems?.find(
                            (item: GradeItem) => item.itemtype === "course"
                          );

                        return (
                          <tr
                            key={course.id}
                            onClick={() => showGradeDetails(course, gradeData)}
                            style={{ cursor: "pointer" }}
                            className="grade-row"
                          >
                            <td>{course.fullname}</td>
                            <td>
                              {gradeQuery.isLoading
                                ? t("student.grades.loading")
                                : courseTotal &&
                                  isValidGrade(courseTotal.gradeformatted)
                                  ? courseTotal.gradeformatted
                                  : "-"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={`${t("student.grades.modal.title")}${selectedCourse?.courseName}`}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        // width={"75vw"}
        className="w-100 w-md-75"
        centered
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
          {selectedCourse &&
            (() => {
              const selectedCourseIndex = courseList.findIndex(
                (course: Course) => course.id === selectedCourse.courseId
              );
              const gradeTableQuery = gradeTableQueries[selectedCourseIndex];

              if (gradeTableQuery?.isLoading) {
                return <div className="text-center">{t("student.grades.modal.loading")}</div>;
              }

              // Lấy dữ liệu đúng format từ API
              const tabledata =
                gradeTableQuery?.data?.tables?.[0]?.tabledata || [];
              
              // Tìm điểm trung bình trước khi filter
              const averageGradeItem = (tabledata as any[])
                .find(
                  (item) =>
                    item &&
                    typeof item === "object" &&
                    item.itemname &&
                    item.itemname.colspan === 1 &&
                    item.grade &&
                    typeof item.grade.content !== "undefined" &&
                    (item.itemname?.content || "").replace(/<[^>]+>/g, "").trim().includes("Tổng khóa học")
                );
              
              const averageGrade = averageGradeItem?.grade?.content || "-";
              
              const gradeItems = (tabledata as any[])
                .filter(
                  (item) =>
                    item &&
                    typeof item === "object" &&
                    item.itemname &&
                    item.itemname.colspan === 1 && // chỉ lấy dòng có điểm, không lấy header/phụ mục
                    item.grade &&
                    typeof item.grade.content !== "undefined" && // chỉ lấy dòng có điểm
                    !(item.itemname?.content || "").replace(/<[^>]+>/g, "").trim().includes("Tổng khóa học") // loại bỏ hàng điểm trung bình
                )
                .map((item, index) => {
                  const rawActivity = (item.itemname?.content || "-")
                    .replace(/<[^>]+>/g, "")
                    .trim();
                  return {
                    key: index,
                    activity: rawActivity,
                    grade: item.grade?.content || "-",
                    weight:item.weight?.content
                      ?.replace(/<[^>]*>/g, "")       // Xoá thẻ HTML
                      ?.replace(/\(.*?\)/g, "")       // Xoá phần trong dấu ngoặc như 
                      ?.replace(/\s+/g, "")           // Xoá khoảng trắng
                      || "-"
                    ,
                    rank:item.rank?.content || "-",
                    range: (() => {
                      const raw = (item.range?.content || "-").replace(/&ndash;|&mdash;/g, "-");
                      if (raw.includes("-")) {
                        return raw.split("-").pop()?.trim() || "-";
                      }
                      return raw;
                    })(),
                    feedback: (item.feedback?.content || "-").replace(/&nbsp;|&nbsp;/g, "-"),
                    gradepass: (item.gradepass?.content || "-" )
                  }
                });
              return (
                <>
                  <Table
                    columns={columns}
                    dataSource={gradeItems}
                    pagination={false}
                    scroll={{ x: true }}
                  />
                  <div className="text-center mt-3 p-3 bg-light rounded">
                    <strong>Điểm trung bình: {averageGrade}</strong>
                  </div>
                </>
              );
            })()}
        </div>
      </Modal>
    </>
  );
};

export default StudentGrades;
